import { SpeakingLevelBadge } from "../../UI/SpeakingLevelBadge.tsx";
import { useState } from "react";
import { formatDate, formatDateTime } from "../../UI/Utils/formatDate.ts";
import dayjs from "dayjs";
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  GiftIcon,
} from "@heroicons/react/24/outline/index.js";
import { ClassRoomDetailScreenPath } from "../../ClassRoom/ClassRoomDetailScreen.tsx";
import {
  getPromoBonusLabel,
  UserClassRoomHistoryItemDto,
  UserClassRoomProgressCheckResultDto,
  UserClassRoomPromoBonusDto,
} from "./types.ts";
import { getRecommendationLabel } from "../ProgressCheck/getRecommendationLabel.tsx";
import { ProgressCheckRecommendation } from "../../gql/graphql.ts";
import { ClassRoomTeacherDetailModal } from "./ClassRoomTeacherDetailModal.tsx";

function Badge({
  label,
  tone,
}: {
  label: string;
  tone: "blue" | "green" | "amber" | "slate" | "rose" | "violet" | "outline";
}) {
  const tones: Record<string, string> = {
    blue: "bg-blue-100 text-blue-800",
    green: "bg-green-100 text-green-800",
    amber: "bg-amber-100 text-amber-800",
    slate: "bg-slate-100 text-slate-700",
    rose: "bg-rose-100 text-rose-800",
    violet: "bg-violet-100 text-violet-800",
    outline: "border border-dashed border-slate-300 text-slate-400",
  };
  return (
    <span
      className={`px-2 py-0.5 text-xs font-semibold rounded-full ${tones[tone]}`}
    >
      {label}
    </span>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={"flex flex-col"}>
      <span className={"text-xs uppercase tracking-wide text-slate-400"}>
        {label}
      </span>
      <span className={"text-sm text-slate-700"}>{children}</span>
    </div>
  );
}

function ScoreRow({ label, value }: { label: string; value: number }) {
  return (
    <div className={"flex items-center justify-between text-sm"}>
      <span className={"text-slate-600"}>{label}</span>
      <span className={"font-medium text-slate-800 tabular-nums"}>{value}</span>
    </div>
  );
}

function PromoDetail({
  promoBonus,
}: {
  promoBonus: UserClassRoomPromoBonusDto;
}) {
  const window = [
    promoBonus.activeFromUtc ? formatDate(promoBonus.activeFromUtc) : null,
    promoBonus.activeToUtc ? formatDate(promoBonus.activeToUtc) : null,
  ];
  return (
    <div
      className={
        "rounded-lg border border-violet-200 bg-violet-50 p-3 space-y-1"
      }
    >
      <p className={"text-sm font-semibold text-slate-800"}>
        {`Gifted by the "${getPromoBonusLabel(promoBonus.type)}" promo`}
      </p>
      {promoBonus.lessonsCountedPerWeek != null && (
        <p className={"text-xs text-slate-600"}>
          Lessons counted per week: <b>{promoBonus.lessonsCountedPerWeek}</b> —
          any extra lesson booked in the same week is free.
        </p>
      )}
      {(window[0] || window[1]) && (
        <p className={"text-xs text-slate-600"}>
          Promo window: {window[0] ?? "—"} → {window[1] ?? "—"}
        </p>
      )}
      {promoBonus.promoIds.length > 0 && (
        <p className={"text-xs text-slate-400 break-all"}>
          Promo ids: {promoBonus.promoIds.join(", ")}
        </p>
      )}
      <p className={"text-xs text-slate-400 break-all"}>
        Plan id: {promoBonus.planId}
      </p>
    </div>
  );
}

function safeRecommendationLabel(recommendation: string): string {
  try {
    return getRecommendationLabel(
      recommendation as ProgressCheckRecommendation,
    );
  } catch {
    return recommendation;
  }
}

function ProgressCheckResultDetail({
  result,
}: {
  result: UserClassRoomProgressCheckResultDto;
}) {
  return (
    <div className={"rounded-lg border border-slate-200 bg-white p-3"}>
      <div className={"flex items-center justify-between mb-2"}>
        <h4
          className={"font-bold text-xs uppercase tracking-wide text-slate-500"}
        >
          Progress check result (from the teacher)
        </h4>
        <Badge
          label={result.passed ? "Passed" : "Not passed"}
          tone={result.passed ? "green" : "rose"}
        />
      </div>
      <div className={"space-y-1"}>
        <ScoreRow label={"Accuracy"} value={result.accuracy} />
        <ScoreRow label={"Comprehension"} value={result.comprehension} />
        <ScoreRow label={"Vocabulary range"} value={result.vocabularyRange} />
        <ScoreRow label={"Fluency"} value={result.fluency} />
        <div className={"flex items-center justify-between text-sm"}>
          <span className={"text-slate-600"}>No show</span>
          <span className={"font-medium text-slate-800"}>
            {result.noShow ? "Yes" : "No"}
          </span>
        </div>
        {result.toLevel && (
          <div className={"flex items-center justify-between text-sm"}>
            <span className={"text-slate-600"}>Moved to level</span>
            <span className={"font-medium text-slate-800"}>
              {result.toLevel}
            </span>
          </div>
        )}
        {result.recommendation.length > 0 && (
          <ul
            className={
              "pt-2 border-t border-slate-100 space-y-1 text-sm text-slate-600"
            }
          >
            {result.recommendation.map((r) => (
              <li key={r} className={"flex gap-2"}>
                <span
                  className={
                    "block h-1.5 w-1.5 bg-slate-400 rounded-full relative top-2 shrink-0"
                  }
                />
                <span>{safeRecommendationLabel(r)}</span>
              </li>
            ))}
          </ul>
        )}
        {result.notes && (
          <p
            className={
              "text-sm text-slate-600 pt-2 border-t border-slate-100 whitespace-pre-wrap"
            }
          >
            {result.notes}
          </p>
        )}
      </div>
    </div>
  );
}

export function UserClassRoomHistoryCard({
  classRoom,
}: {
  classRoom: UserClassRoomHistoryItemDto;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showTeacherModal, setShowTeacherModal] = useState(false);

  const teacherName =
    classRoom.teacherGivenName || classRoom.teacherFamilyName
      ? `${classRoom.teacherGivenName ?? ""} ${classRoom.teacherFamilyName ?? ""}`.trim()
      : classRoom.teacherId;

  const detailPath = ClassRoomDetailScreenPath.replace(
    ":classRoomId",
    classRoom.classRoomId,
  );

  const teacherFeedback = classRoom.isProgressCheck
    ? classRoom.progressCheckResult
    : classRoom.userFeedback;

  return (
    <div
      className={[
        "bg-white rounded-lg border border-slate-200 border-l-4 overflow-hidden transition-colors",
        classRoom.isProgressCheck ? "border-l-amber-400" : "border-l-blue-400",
      ].join(" ")}
    >
      {showTeacherModal && (
        <ClassRoomTeacherDetailModal
          classRoom={classRoom}
          onClose={() => setShowTeacherModal(false)}
        />
      )}
      <div className={"flex items-start"}>
        <button
          onClick={() => setExpanded((v) => !v)}
          className={"flex-1 text-left p-4 hover:bg-slate-50 transition-colors"}
        >
          <div className={"flex items-start gap-3"}>
            {expanded ? (
              <ChevronDownIcon
                className={"w-4 h-4 text-slate-400 mt-1 shrink-0"}
              />
            ) : (
              <ChevronRightIcon
                className={"w-4 h-4 text-slate-400 mt-1 shrink-0"}
              />
            )}
            <div className={"space-y-1.5"}>
              <p className={"font-semibold text-slate-800"}>
                {formatDateTime(classRoom.startUtc, { withWeekday: true })}
                <span className={"font-normal text-slate-400"}>
                  {" "}
                  – {dayjs(classRoom.endUtc).format("HH:mm")}
                </span>
              </p>
              <p className={"text-sm text-slate-600"}>
                Teacher: <b>{teacherName}</b>
              </p>
              <div className={"flex flex-wrap items-center gap-1.5"}>
                <Badge
                  label={
                    classRoom.isProgressCheck ? "Progress check" : "Lesson"
                  }
                  tone={classRoom.isProgressCheck ? "amber" : "blue"}
                />
                <Badge
                  label={classRoom.is1on1 ? "1 to 1" : "Group"}
                  tone={"slate"}
                />
                <Badge
                  label={classRoom.userGroupName ?? classRoom.userGroupId}
                  tone={"slate"}
                />
                {classRoom.giftedByPromo && (
                  <Badge
                    label={
                      classRoom.promoBonus
                        ? `Gifted · ${getPromoBonusLabel(classRoom.promoBonus.type)}`
                        : "Gifted"
                    }
                    tone={"violet"}
                  />
                )}
                {classRoom.userNoShow && (
                  <Badge label={"No show"} tone={"rose"} />
                )}
                <Badge
                  label={
                    teacherFeedback
                      ? "Teacher feedback ✓"
                      : "No teacher feedback"
                  }
                  tone={teacherFeedback ? "green" : "outline"}
                />
                <Badge
                  label={
                    classRoom.userReview
                      ? "Student feedback ✓"
                      : "No student feedback"
                  }
                  tone={classRoom.userReview ? "green" : "outline"}
                />
              </div>
            </div>
          </div>
        </button>
        <a
          href={detailPath}
          target={"_blank"}
          rel={"noreferrer"}
          onClick={(e) => e.stopPropagation()}
          className={
            "p-4 text-xs text-primary underline hover:brightness-110 shrink-0"
          }
        >
          Details
        </a>
      </div>

      {expanded && (
        <div
          className={"border-t border-slate-100 p-4 space-y-4 bg-slate-50/50"}
        >
          <div className={"grid grid-cols-2 md:grid-cols-3 gap-3"}>
            <DetailRow label={"Type"}>
              {classRoom.isProgressCheck ? "Progress check" : "Lesson"} (
              {classRoom.classRoomType})
            </DetailRow>
            <DetailRow label={"Level"}>
              <SpeakingLevelBadge level={classRoom.speakingLevel} />
            </DetailRow>
            <DetailRow label={"Topic"}>{classRoom.topicName ?? "—"}</DetailRow>
            <DetailRow label={"Timezone"}>
              {classRoom.classRoomTimezone}
            </DetailRow>
            <DetailRow label={"Students booked"}>
              {classRoom.participantsCount} / {classRoom.capacity}
            </DetailRow>
            <DetailRow label={"Students attended"}>
              {classRoom.attendedParticipantsCount}
              {classRoom.feedbackCount === 0 && (
                <span className={"text-xs text-slate-400"}> (no feedback)</span>
              )}
            </DetailRow>
            <DetailRow label={"No shows"}>
              {classRoom.noShowParticipantsCount}
            </DetailRow>
            <DetailRow label={"Feedbacks given"}>
              {classRoom.feedbackCount} / {classRoom.participantsCount}
            </DetailRow>
            <DetailRow label={"Teacher"}>
              <button
                onClick={() => setShowTeacherModal(true)}
                className={"text-primary underline hover:brightness-110"}
              >
                {teacherName}
              </button>
            </DetailRow>
            <DetailRow label={"Classroom id"}>
              <span className={"break-all text-xs"}>
                {classRoom.classRoomId}
              </span>
            </DetailRow>
          </div>

          {!classRoom.giftedByPromo ? null : classRoom.promoBonus ? (
            <PromoDetail promoBonus={classRoom.promoBonus} />
          ) : (
            <div
              className={
                "rounded-lg border border-violet-200 bg-violet-50 p-3 flex items-center gap-2"
              }
            >
              <GiftIcon className={"w-4 h-4 text-violet-600"} />
              <p className={"text-sm text-slate-700"}>
                Lesson gifted by a promo. The promo config is no longer on the
                plan, so the promo name cannot be resolved.
              </p>
            </div>
          )}

          <div className={"grid grid-cols-1 md:grid-cols-2 gap-3"}>
            {classRoom.isProgressCheck &&
              (classRoom.progressCheckResult ? (
                <ProgressCheckResultDetail
                  result={classRoom.progressCheckResult}
                />
              ) : (
                <div
                  className={"rounded-lg border border-slate-200 bg-white p-3"}
                >
                  <h4
                    className={
                      "font-bold text-xs uppercase tracking-wide text-slate-500 mb-2"
                    }
                  >
                    Progress check result (from the teacher)
                  </h4>
                  <p className={"text-sm text-slate-400 italic"}>
                    The teacher has not left a progress check result for this
                    user.
                  </p>
                </div>
              ))}

            {(!classRoom.isProgressCheck || classRoom.userFeedback) && (
              <div
                className={"rounded-lg border border-slate-200 bg-white p-3"}
              >
                <h4
                  className={
                    "font-bold text-xs uppercase tracking-wide text-slate-500 mb-2"
                  }
                >
                  Lesson feedback (from the teacher)
                </h4>
                {classRoom.userFeedback ? (
                  <div className={"space-y-1"}>
                    <ScoreRow
                      label={"Participation"}
                      value={classRoom.userFeedback.participation}
                    />
                    <ScoreRow
                      label={"Target language"}
                      value={classRoom.userFeedback.targetLanguage}
                    />
                    <div
                      className={"flex items-center justify-between text-sm"}
                    >
                      <span className={"text-slate-600"}>No show</span>
                      <span className={"font-medium text-slate-800"}>
                        {classRoom.userFeedback.noShow ? "Yes" : "No"}
                      </span>
                    </div>
                    {classRoom.userFeedback.differentSpeakingLevelSuggested && (
                      <div
                        className={"flex items-center justify-between text-sm"}
                      >
                        <span className={"text-slate-600"}>
                          Suggested level
                        </span>
                        <span className={"font-medium text-slate-800"}>
                          {
                            classRoom.userFeedback
                              .differentSpeakingLevelSuggested
                          }
                        </span>
                      </div>
                    )}
                    {classRoom.userFeedback.notes && (
                      <p
                        className={
                          "text-sm text-slate-600 pt-2 border-t border-slate-100 whitespace-pre-wrap"
                        }
                      >
                        {classRoom.userFeedback.notes}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className={"text-sm text-slate-400 italic"}>
                    The teacher has not left a lesson feedback for this user.
                  </p>
                )}
              </div>
            )}

            <div className={"rounded-lg border border-slate-200 bg-white p-3"}>
              <h4
                className={
                  "font-bold text-xs uppercase tracking-wide text-slate-500 mb-2"
                }
              >
                Lesson feedback (left by the student)
              </h4>
              {classRoom.userReview ? (
                <div className={"space-y-1"}>
                  <ScoreRow
                    label={"General lesson"}
                    value={classRoom.userReview.generalLessonScore}
                  />
                  <ScoreRow
                    label={"Teacher clearance"}
                    value={classRoom.userReview.teacherClearanceScore}
                  />
                  <ScoreRow
                    label={"Teacher involvement"}
                    value={classRoom.userReview.teacherInvolvementScore}
                  />
                  <ScoreRow
                    label={"Teacher comfortable"}
                    value={classRoom.userReview.teacherComfortableScore}
                  />
                  <ScoreRow
                    label={"Topic usefulness"}
                    value={classRoom.userReview.topicUsefulnessScore}
                  />
                  <ScoreRow
                    label={"Topic difficulty"}
                    value={classRoom.userReview.topicDifficultyScore}
                  />
                  {classRoom.userReview.reviewNotes && (
                    <p
                      className={
                        "text-sm text-slate-600 pt-2 border-t border-slate-100 whitespace-pre-wrap"
                      }
                    >
                      {classRoom.userReview.reviewNotes}
                    </p>
                  )}
                  <p className={"text-xs text-slate-400 pt-1"}>
                    Left on {formatDateTime(classRoom.userReview.reviewedAtUtc)}
                  </p>
                </div>
              ) : (
                <p className={"text-sm text-slate-400 italic"}>
                  The student has not left a feedback for this lesson.
                </p>
              )}
            </div>
          </div>

          <div>
            <a
              href={detailPath}
              target={"_blank"}
              rel={"noreferrer"}
              className={
                "inline-flex items-center gap-1.5 text-sm text-blue-600 underline"
              }
            >
              <ArrowTopRightOnSquareIcon className={"w-4 h-4"} />
              Open classroom detail page
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
